import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { console_ } from '../services/api';
import { n, dateFr } from '../services/format';
import { Erreur, Chargement, messageErreur } from './Ui';

// Tableau de bord : couverture par société × finalité, expirations à 30 j,
// demandes en cours, état de la chaîne de preuves.
export default function TableauDeBord() {
  const [stats, setStats] = useState(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    console_.stats().then((r) => setStats(r.data)).catch((e) => setErreur(messageErreur(e)));
  }, []);

  if (erreur) return <Erreur message={erreur} />;
  if (!stats) return <Chargement />;
  const { couverture, relations, totaux, activite, modes } = stats;

  const societes = [...new Set(couverture.map((c) => c.societe))];
  const finalites = [...new Map(couverture.map((c) => [c.finalite, c.libelle])).entries()];
  const cellule = (soc, fin) => couverture.find((c) => c.societe === soc && c.finalite === fin) || {};
  const expirent = couverture.reduce((s, c) => s + Number(c.expirent_30j || 0), 0);
  const parJour = {};
  for (const a of activite) {
    const j = String(a.jour).slice(0, 10);
    parJour[j] = parJour[j] || {};
    parJour[j][a.etat] = Number(a.n);
  }

  return (
    <div>
      <h1 className="rg-titre-page">Tableau de bord</h1>
      {(modes.sms !== 'actif' || modes.email !== 'actif') && (
        <div className="rg-alerte">Envois en mode <strong>{modes.sms}</strong> (SMS) / <strong>{modes.email}</strong> (e-mail) : aucun message réel ne part de cet environnement.</div>
      )}
      <div className="rg-cartes">
        <Carte label="Identifiants" valeur={n(totaux.identifiants)} sous={`${n(totaux.telephones)} tél. · ${n(totaux.emails)} e-mail`} />
        <Carte label="Preuves" valeur={n(totaux.preuves)} sous={totaux.dernier_sceau_jour ? `scellé le ${dateFr(totaux.dernier_sceau_jour)}` : 'aucun sceau'} />
        <Carte label="Demandes en cours" valeur={n(totaux.demandes_en_cours)} sous={`${n(totaux.demandes_repondues)} réponses / ${n(totaux.demandes_envoyees)} envoyées`} />
        <Carte label="Expirent sous 30 j" valeur={n(expirent)} sous="renouvellement automatique" alerte={expirent > 0} />
      </div>

      <section className="rg-section">
        <h2>Couverture par société et finalité<small>accordés · refusés/retirés · expirés</small></h2>
        <div className="rg-defile">
          <table className="rg-tableau">
            <thead>
              <tr>
                <th>Société</th>
                {finalites.map(([code, lib]) => <th key={code} title={lib}>{code.replace('prospection_', '')}</th>)}
                <th>Base client 36 mois</th>
              </tr>
            </thead>
            <tbody>
              {societes.map((soc) => {
                const rel = relations.find((r) => r.societe === soc) || {};
                return (
                  <tr key={soc}>
                    <td><strong>{couverture.find((c) => c.societe === soc)?.raison_sociale}</strong></td>
                    {finalites.map(([code]) => {
                      const c = cellule(soc, code);
                      const tot = Number(c.accordes || 0) + Number(c.refuses || 0) + Number(c.retires || 0) + Number(c.expires || 0);
                      return (
                        <td key={code}>
                          <div className="rg-num" style={{ textAlign: 'left' }}>
                            <span className="rg-etat rg-etat--accorde">{n(c.accordes)}</span>{' '}
                            <span className="rg-etat rg-etat--refuse">{n(Number(c.refuses || 0) + Number(c.retires || 0))}</span>{' '}
                            <span className="rg-etat rg-etat--expire">{n(c.expires)}</span>
                          </div>
                          {tot > 0 && (
                            <div className="rg-barre" style={{ marginTop: 6 }}>
                              <span className="b-accorde" style={{ width: `${(100 * Number(c.accordes || 0)) / tot}%` }} />
                              <span className="b-refuse" style={{ width: `${(100 * (Number(c.refuses || 0) + Number(c.retires || 0))) / tot}%` }} />
                              <span className="b-expire" style={{ width: `${(100 * Number(c.expires || 0)) / tot}%` }} />
                            </div>
                          )}
                          {Number(c.expirent_30j) > 0 && <div className="rg-info">{n(c.expirent_30j)} expirent sous 30 j</div>}
                        </td>
                      );
                    })}
                    <td className="rg-num">{n(rel.actifs_36m)} <span className="rg-info" style={{ display: 'inline' }}>/ {n(rel.identifiants)}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rg-section">
        <h2>Activité des 30 derniers jours<small>événements de consentement par jour</small></h2>
        {Object.keys(parJour).length === 0 ? <p className="rg-info">Aucun événement.</p> : (
          <div className="rg-defile">
            <table className="rg-tableau">
              <thead><tr><th>Jour</th><th className="rg-num">Accordés</th><th className="rg-num">Refusés</th><th className="rg-num">Retirés</th><th className="rg-num">Expirés</th></tr></thead>
              <tbody>
                {Object.entries(parJour).reverse().map(([j, v]) => (
                  <tr key={j}><td>{dateFr(j)}</td><td className="rg-num">{n(v.accorde)}</td><td className="rg-num">{n(v.refuse)}</td><td className="rg-num">{n(v.retire)}</td><td className="rg-num">{n(v.expire)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="rg-info">Chaîne de preuves : dernier sceau {totaux.dernier_sceau ? <code className="rg-mono">{String(totaux.dernier_sceau).slice(0, 16)}…</code> : 'aucun'}. Voir <Link to="/journal">Journal</Link>.</p>
    </div>
  );
}

function Carte({ label, valeur, sous, alerte }) {
  return (
    <div className="rg-carte">
      <div className="rg-carte-label">{label}</div>
      <div className="rg-carte-valeur" style={alerte ? { color: '#d97706' } : undefined}>{valeur}</div>
      {sous && <div className="rg-carte-sous">{sous}</div>}
    </div>
  );
}
